// Contractor payments tab: the yearly supplier-payment rollup behind the
// 1099 filing. Read-only — no filing endpoint exists, so this is the
// numbers table, not the filing itself.
//
// The report carries supplier record IDs and tax identifiers, not names
// (the backend joins no CRM name), so a row never shows a raw UUID as its
// primary label: the tax identifier is used when present, otherwise a short
// record reference, with the full contact ID kept in the title attribute.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  getTax1099Report,
  type Tax1099Report,
  type Tax1099Vendor,
} from "@/lib/api";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

/** Readable supplier label from data the report actually carries. */
function supplierLabel(vendor: Tax1099Vendor): string {
  const taxId = vendor.tax_identifier?.trim();
  if (taxId) return `Tax ID ${taxId}`;
  return `Supplier ${vendor.contact_id.slice(0, 8)}`;
}

export function ContractorTab() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [threshold, setThreshold] = useState("");
  const [applied, setApplied] = useState({ year: thisYear, threshold: "" });
  const [report, setReport] = useState<Tax1099Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback((query: { year: number; threshold: string }) => {
    setLoading(true);
    setError(null);
    const threshold_cents = query.threshold.trim()
      ? toMinor(query.threshold)
      : null;
    if (query.threshold.trim() && threshold_cents === null) {
      setError("Threshold must be an amount of zero or more.");
      setLoading(false);
      return;
    }
    getTax1099Report({
      tax_year: query.year,
      ...(threshold_cents !== null ? { threshold_cents } : {}),
    })
      .then(setReport)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = Number(year);
    if (!Number.isInteger(parsed) || parsed < 2000 || parsed > 2100) {
      setError("Enter a tax year like 2026.");
      return;
    }
    setApplied({ year: parsed, threshold });
  };

  return (
    <div className="grid gap-4">
      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => run(applied)}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={submit}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="t09-year" className="grid gap-1 text-xs font-medium">
          Tax year
          <Input
            id="t09-year"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="t09-threshold" className="grid gap-1 text-xs font-medium">
          Reportable from ({DISPLAY_CURRENCY}, optional)
          <Input
            id="t09-threshold"
            type="number"
            min="0"
            step="0.01"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="600.00"
            disabled={loading}
            aria-describedby="t09-threshold-help"
          />
        </label>
        <Button type="submit" disabled={loading}>
          {loading ? "Running…" : "Run report"}
        </Button>
        <p
          id="t09-threshold-help"
          className="w-full text-xs text-[var(--muted-foreground)]"
        >
          Only suppliers paid at least this much count as reportable; leave it
          blank to use the standard threshold. The table shows totals per
          supplier — the 1099 filing itself is filed elsewhere.
        </p>
      </form>

      {loading && report === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : report ? (
        <div className="grid gap-4">
          <div
            className="surface overflow-hidden rounded-2xl"
            data-testid="contractors-summary"
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Reportable suppliers</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {report.reportable_count}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reportable total</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(report.reportable_total_cents)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold">
                    Paid to all suppliers
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {money(report.grand_total_cents)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {report.vendors.length === 0 ? (
            <EmptyState
              title={`No supplier payments recorded for ${report.tax_year}`}
              reason="Bill payments to suppliers will appear here once they are recorded; the 1099 report only counts non-card payments."
            />
          ) : (
            <>
              <section
                aria-label="Suppliers"
                className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
                data-testid="contractors-table"
              >
                <h3 className="bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
                  Suppliers · {report.tax_year} · reportable from{" "}
                  {money(report.threshold_cents)}
                </h3>
                <div className="table-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead className="text-right">Payments</TableHead>
                        <TableHead className="text-right">Total paid</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.vendors.map((vendor) => (
                        <TableRow key={vendor.contact_id}>
                          <TableCell>
                            <span
                              className="font-medium"
                              title={`Supplier record ${vendor.contact_id}`}
                            >
                              {supplierLabel(vendor)}
                            </span>
                            {vendor.tax_identifier?.trim() ? (
                              <span
                                className="block font-mono text-xs text-[var(--muted-foreground)]"
                                title={`Supplier record ${vendor.contact_id}`}
                              >
                                Supplier {vendor.contact_id.slice(0, 8)}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {vendor.payment_count}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(vendor.total_paid_cents)}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={vendor.reportable ? "info" : "idle"}
                              label={
                                vendor.reportable
                                  ? "Reportable"
                                  : "Below threshold"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>

              <div
                className="stack-list grid gap-3 min-[64rem]:hidden"
                data-testid="contractors-stack"
              >
                {report.vendors.map((vendor) => (
                  <article
                    key={vendor.contact_id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <span
                        className="record-title font-medium"
                        title={`Supplier record ${vendor.contact_id}`}
                      >
                        {supplierLabel(vendor)}
                      </span>
                      <span className="tabular-nums">
                        {money(vendor.total_paid_cents)}
                      </span>
                    </div>
                    <CardField label="Supplier record">
                      <span
                        className="font-mono text-xs"
                        title={`Supplier record ${vendor.contact_id}`}
                      >
                        {vendor.contact_id.slice(0, 8)}
                      </span>
                    </CardField>
                    <CardField label="Payments">
                      <span className="tabular-nums">
                        {vendor.payment_count}
                      </span>
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={vendor.reportable ? "info" : "idle"}
                        label={
                          vendor.reportable ? "Reportable" : "Below threshold"
                        }
                      />
                    </CardField>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
